/**
 * Build with AI drafts (docs/design/web/WEB_BUILD_PATTERN_PLAN.md §3): the model proposes an entity with
 * propose_draft, the chat shows it as a card. A draft arrives live as a `draft_updated` event and is
 * saved as the tool's result message, which is what rebuilds the card after a reload.
 */

import type { DraftUpdatedData, LinkedEntity, SrcSchemasChatMessageResponse } from "@/api";

export const DRAFT_TOOL = "propose_draft";

export interface DraftView {
  /** The propose_draft call id. */
  draftId: string;
  entityType: string;
  draft: Record<string, unknown>;
  /** Set when the draft changes an existing entity (Save) instead of creating one (Create). */
  targetUuid: string | null;
  missingFields: string[];
  /** In the ask_user question format. */
  questions: unknown[];
}

/** A draft rebuilt from its saved tool message; it renders under the reply `messageUuid` (null: after the thread). */
export interface SavedDraft extends DraftView {
  at: string;
  messageUuid: string | null;
  /** The user has sent a message since (its questions are behind them). */
  answered: boolean;
  /** What its card created or saved, recorded on the draft (PATCH .../drafts/{id}/applied). */
  applied: { entityUuid: string; at: string } | null;
}

export type DraftCardState = "active" | "superseded" | "created" | "updated";

export const draftFromEvent = (data: DraftUpdatedData): DraftView => ({
  draftId: data.draft_id,
  entityType: data.entity_type,
  draft: data.draft ?? {},
  targetUuid: data.target_uuid ?? null,
  missingFields: data.missing_fields ?? [],
  questions: data.questions ?? [],
});

interface DraftContent {
  status?: string;
  entity_type?: string;
  draft?: Record<string, unknown>;
  target_uuid?: string | null;
  missing_fields?: string[];
  questions?: unknown[];
}

function parseDraft(draftId: string, content: string): DraftView | null {
  try {
    const body = JSON.parse(content) as DraftContent;
    if (body.status !== "draft_proposed" || !body.entity_type || !body.draft) return null;
    return draftFromEvent({ ...body, draft_id: draftId, entity_type: body.entity_type });
  } catch {
    return null;
  }
}

function appliedOf(metadata: Record<string, unknown> | null | undefined): SavedDraft["applied"] {
  const applied = metadata?.applied as { entity_uuid?: unknown; at?: unknown } | undefined;
  return typeof applied?.entity_uuid === "string" && typeof applied.at === "string"
    ? { entityUuid: applied.entity_uuid, at: applied.at }
    : null;
}

/** Saved propose_draft results, each attached to the next reply with text (where the thread shows it). */
export function draftsFromMessages(messages: readonly SrcSchemasChatMessageResponse[]): SavedDraft[] {
  let out: SavedDraft[] = [];
  let waiting: SavedDraft[] = [];
  for (const m of messages) {
    if (m.role === "tool" && m.toolCallId && m.messageMetadata?.tool_name === DRAFT_TOOL) {
      const parsed = parseDraft(m.toolCallId, m.content);
      if (parsed) {
        waiting.push({
          ...parsed,
          at: m.createdAt,
          messageUuid: null,
          answered: false,
          applied: appliedOf(m.messageMetadata),
        });
      }
    } else if (m.role === "assistant" && m.content.trim() && waiting.length > 0) {
      out.push(...waiting.map((d) => ({ ...d, messageUuid: m.uuid })));
      waiting = [];
    } else if (m.role === "user") {
      out = [...out, ...waiting].map((d) => ({ ...d, answered: true }));
      waiting = [];
    }
  }
  return [...out, ...waiting];
}

/** One slot per entity being built: each new entity of a type, and each existing entity being changed. */
export const draftKey = (d: Pick<DraftView, "entityType" | "targetUuid">) => `${d.entityType}:${d.targetUuid ?? "new"}`;

/** The newest draft in each slot keeps its actions; live (streaming) drafts are newer than saved ones. */
export function activeDraftIds(saved: readonly DraftView[], live: readonly DraftView[] = []): Set<string> {
  const latest = new Map<string, string>();
  for (const d of [...saved, ...live]) latest.set(draftKey(d), d.draftId);
  return new Set(latest.values());
}

const time = (iso: string) => Date.parse(iso);

/**
 * Created/Updated survive a reload: the card records what it created or saved on the draft. A create draft
 * without that record (made through Open in form, which links the chat) was created when an entity of its type
 * was linked after it and before the next create draft of that type. An entity's own timestamps say nothing:
 * using, running or syncing it moves them too. A deleted entity takes its Created / Updated with it.
 */
export function cardState(
  draft: SavedDraft,
  all: readonly SavedDraft[],
  linked: readonly LinkedEntity[],
  active: ReadonlySet<string>,
): { state: DraftCardState; entityUuid?: string } {
  // Only while the entity is still there: a deleted one would leave an Open to a missing page.
  const applied = draft.applied && linked.some((e) => e.uuid === draft.applied?.entityUuid) ? draft.applied : null;
  if (applied) return { state: draft.targetUuid ? "updated" : "created", entityUuid: applied.entityUuid };
  const at = time(draft.at);
  if (!draft.targetUuid) {
    const next = all
      .filter((d) => d.entityType === draft.entityType && !d.targetUuid && time(d.at) > at)
      .reduce((min, d) => Math.min(min, time(d.at)), Number.POSITIVE_INFINITY);
    const made = linked.find(
      (e) => e.entityType === draft.entityType && time(e.createdAt) >= at && time(e.createdAt) < next,
    );
    if (made) return { state: "created", entityUuid: made.uuid };
  }
  return { state: active.has(draft.draftId) ? "active" : "superseded" };
}
